import React, { useState, useContext, useEffect } from 'react';
import { Paper, Typography, Button, TextField, Box, Container, Dialog, DialogTitle, DialogContent } from '@mui/material';
import ResumePersonalization from './ResumePersonalization';
import API_BASE_URL from '../config';
import { UserContext } from '../UserContext';

export default function ExtracurricularService() {
  const [interests, setInterests] = useState('');
  const [location, setLocation] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [plan, setPlan] = useState([]);
  const { user } = useContext(UserContext) || {};

  useEffect(() => {
    // Fetch user's plan on mount
    const fetchPlan = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/plan`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPlan(data);
        }
      } catch (err) {}
    };
    fetchPlan();
  }, []);

  const handleResumeTextChange = (e) => setResumeText(e.target.value);
  const handleResumeFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setResumeFile(e.target.files[0]);
      setResumeText('');
    } else {
      setResumeFile(null);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setRecommendations([]);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('You must be logged in.');
      const response = await fetch(`${API_BASE_URL}/recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          interests,
          location
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get recommendations');
      }
      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add to Plan handler
  const handleAddToPlan = async (rec) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_id: rec.item_id || rec.name.replace(/\s+/g, '_').toLowerCase(),
          item_type: 'ec',
          name: rec.name,
          time_commitment: rec.time_commitment || rec.duration || '',
          details: rec
        })
      });
      const data = await response.json();
      if (data.success) {
        setPlan(plan => [...plan, data.plan_item]);
      } else {
        setError(data.error || 'Failed to add to plan');
      }
    } catch (err) {
      setError('Failed to add to plan');
    }
  };

  // Check if item is already in plan
  const isInPlan = (rec) => plan.some(item => item.item_id === (rec.item_id || rec.name.replace(/\s+/g, '_').toLowerCase()) && item.item_type === 'ec');

  return (
    <Container maxWidth="lg" sx={{ py: 6, pt: 10 }}>
      {/* What Drives You Section */}
      <Box mb={6}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
            What drives you?
          </Typography>
          <Typography align="center" color="textSecondary" sx={{ mb: 3 }}>
            Share your passions and location for personalized extracurricular opportunities tailored to your interests and area!
          </Typography>
          <TextField
            label="Tell us about your interests... (e.g., computer science, robotics, environmental activism, creative writing, sports, music, entrepreneurship...)"
            value={interests}
            onChange={e => setInterests(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Location"
            value={location}
            onChange={e => setLocation(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
          />
        </Paper>
      </Box>
      {/* Resume Personalization Section */}
      <ResumePersonalization
        resumeText={resumeText}
        resumeFile={resumeFile}
        onResumeTextChange={handleResumeTextChange}
        onResumeFileChange={handleResumeFileChange}
        error={error}
      />
      {/* Discover Your Future Button */}
      <Box mt={8} display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{
            fontWeight: 700,
            fontSize: 28,
            borderRadius: 4,
            px: 8,
            py: 2.5,
            boxShadow: '0 6px 24px 0 rgba(102,126,234,0.25)',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            textTransform: 'none',
            letterSpacing: 1.5
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          Shape Your Future
        </Button>
      </Box>
      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <Box mt={8}>
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
            Personalized Recommendations
          </Typography>
          <Box display="flex" flexDirection="column" gap={3}>
            {recommendations.map((rec, idx) => (
              <Paper key={idx} elevation={2} sx={{ p: 2, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {rec.name}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {rec.type}
                </Typography>
                <Button variant="outlined" size="small" sx={{ mt: 'auto', mr: 2 }} onClick={() => setSelectedRecommendation(rec)}>
                  Learn More
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color={isInPlan(rec) ? 'success' : 'primary'}
                  sx={{ mt: 1 }}
                  onClick={() => handleAddToPlan(rec)}
                  disabled={isInPlan(rec)}
                >
                  {isInPlan(rec) ? 'Added' : 'Add to Plan'}
                </Button>
              </Paper>
            ))}
          </Box>
          {/* Recommendation Detail Dialog */}
          <Dialog open={!!selectedRecommendation} onClose={() => setSelectedRecommendation(null)} maxWidth="sm" fullWidth>
            {selectedRecommendation && (
              <>
                <DialogTitle>
                  {selectedRecommendation.name}
                </DialogTitle>
                <DialogContent dividers>
                  <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                    {selectedRecommendation.type} | {selectedRecommendation.location} | {selectedRecommendation.difficulty}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedRecommendation.description}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Duration:</strong> {selectedRecommendation.duration}<br/>
                    <strong>Eligibility:</strong> {selectedRecommendation.eligibility}<br/>
                    <strong>Cost:</strong> {selectedRecommendation.cost}<br/>
                    <strong>Deadline:</strong> {selectedRecommendation.application_deadline}<br/>
                    <strong>Benefits:</strong> {selectedRecommendation.benefits}<br/>
                    {selectedRecommendation.website && (
                      <span>
                        <strong>Website:</strong> <a href={selectedRecommendation.website} target="_blank" rel="noopener noreferrer">{selectedRecommendation.website}</a><br/>
                      </span>
                    )}
                    <strong>Contact:</strong> {selectedRecommendation.contact_info}
                  </Typography>
                </DialogContent>
                <Box display="flex" justifyContent="flex-end" p={2}>
                  <Button onClick={() => setSelectedRecommendation(null)} variant="contained">Close</Button>
                </Box>
              </>
            )}
          </Dialog>
        </Box>
      )}
    </Container>
  );
} 